import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from 'lucide-react';
import type { UserDetails } from '@/api/users/users.types';

interface RemoveMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: UserDetails | null;
  onConfirm: (member: UserDetails) => Promise<void>;
}

export const RemoveMemberDialog = ({
  open,
  onOpenChange,
  member,
  onConfirm,
}: RemoveMemberDialogProps) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  
  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsDeleting(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!member) return;
    
    setIsDeleting(true);
    try {
      await onConfirm(member);
      // Let the parent component handle the dialog close
    } catch (
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _error
    ) {
      // Error will be handled by parent
      // Only close the dialog if it's still open
      if (open) {
        onOpenChange(false);
      }
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleDialogInteraction = (open: boolean) => {
    // Only let the user close the dialog if we're not in the middle of deleting
    if (!isDeleting || !open) {
      onOpenChange(open);
    }
  };

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={handleDialogInteraction}>
      <DialogContent onEscapeKeyDown={(e) => isDeleting && e.preventDefault()} onPointerDownOutside={(e) => isDeleting && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Remove Member</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove {member.first_name} {member.last_name}? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Removing...
              </>
            ) : (
              'Remove Member'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
